import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import EmployeePicker from "@/components/EmployeePicker";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useConfirm } from "@/components/ConfirmProvider";
import { Plus, X, Check, Ban, Trash2, Inbox } from "lucide-react";

export default function Leaves() {
    const { user } = useAuth();
    const isEmployee = user?.role === "employee";
    const { confirm } = useConfirm();
    const [leaves, setLeaves] = useState([]);
    const [employees, setEmployees] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState("all");
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState({ employee_id: "", leave_type: "Paid", start_date: "", end_date: "", reason: "" });
    const [saving, setSaving] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const [l, e] = await Promise.all([
                api.get("/leaves", { params: { status: filter } }),
                api.get("/employees"),
            ]);
            setLeaves(l.data);
            setEmployees(e.data);
        } catch (err) {
            toast.error("Failed to load leaves");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, [filter]);

    const setStatus = async (id, status) => {
        // Map legacy status → workflow action
        const action = status === "approved" ? "approve" : status === "rejected" ? "reject" : status;
        try {
            await api.put(`/leaves/${id}`, { action });
            toast.success(`Leave ${status}`);
            load();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Failed to update");
        }
    };

    const onDelete = async (id) => {
        const ok = await confirm({
            title: "Delete this leave request?",
            description: "This action cannot be undone.",
            confirmLabel: "Delete",
            danger: true,
        });
        if (!ok) return;
        try {
            await api.delete(`/leaves/${id}`);
            toast.success("Deleted");
            load();
        } catch (e) {
            toast.error("Failed to delete");
        }
    };

    const onCreate = async (e) => {
        e.preventDefault();
        if (!form.employee_id) return toast.error("Please select an employee");
        setSaving(true);
        try {
            await api.post("/leaves", form);
            toast.success("Leave request created");
            setOpen(false);
            setForm({ employee_id: "", leave_type: "Paid", start_date: "", end_date: "", reason: "" });
            load();
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || "Create failed");
        } finally {
            setSaving(false);
        }
    };

    const tabs = [
        { key: "all", label: "All" },
        { key: "pending", label: "Pending" },
        { key: "approved", label: "Approved" },
        { key: "rejected", label: "Rejected" },
    ];

    return (
        <div className="px-8 md:px-12 py-12 md:py-16" data-testid="leaves-page">
            <div className="flex items-end justify-between flex-wrap gap-6">
                <div>
                    <div className="overline">Time off</div>
                    <h1 className="mt-3 font-display font-black tracking-tighter text-4xl sm:text-5xl text-ink leading-[1.02]">
                        Leave requests
                    </h1>
                </div>
                <button
                    onClick={() => setOpen(true)}
                    className="inline-flex items-center gap-2 bg-klein text-white px-5 py-3 text-sm hover:bg-klein/90"
                    data-testid="add-leave-button"
                >
                    <Plus className="w-4 h-4" /> New request
                </button>
            </div>

            <div className="mt-10 flex items-center gap-0 border-b border-ink/10" data-testid="leaves-filter-tabs">
                {tabs.map((t) => (
                    <button
                        key={t.key}
                        onClick={() => setFilter(t.key)}
                        data-testid={`leaves-tab-${t.key}`}
                        className={`px-5 py-3 text-sm border-b-2 -mb-px transition-colors ${
                            filter === t.key ? "border-klein text-ink font-medium" : "border-transparent text-ink/50 hover:text-ink"
                        }`}
                    >
                        {t.label}
                    </button>
                ))}
            </div>

            <div className="mt-6 flex items-center gap-3 px-4 py-3 rounded-xl bg-indigo-50/60 border border-indigo-200 text-indigo-900 text-sm dark:bg-indigo-500/10 dark:border-indigo-500/30 dark:text-indigo-200" data-testid="leaves-workflow-hint">
                <Inbox className="w-4 h-4 shrink-0" />
                <span className="flex-1">
                    {isEmployee
                        ? "Your leave goes through HR verification then Department Lead approval. You'll receive an email at each stage."
                        : <>Approval workflow: <strong>HR verifies</strong> → <strong>Department Lead approves</strong>. Use the Pending Approvals inbox to action requests at your stage.</>}
                </span>
                {!isEmployee && (
                <Link to="/pending-approvals" className="underline font-medium" data-testid="leaves-open-pending-approvals">
                    Open inbox →
                </Link>
                )}
            </div>

            <div className="mt-6 bg-white border border-ink/10 overflow-auto max-h-[420px]">
                <table className="w-full border-collapse">
                    <thead>
                        <tr className="border-b-2 border-ink/20">
                            <th className="text-left overline px-6 py-4">Employee</th>
                            <th className="text-left overline px-6 py-4">Type</th>
                            <th className="text-left overline px-6 py-4">Dates</th>
                            <th className="text-left overline px-6 py-4 hidden md:table-cell">Reason</th>
                            <th className="text-left overline px-6 py-4">Status</th>
                            <th className="text-right overline px-6 py-4">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <tr><td colSpan="6" className="px-6 py-20 text-center text-sm text-ink/40">Loading…</td></tr>
                        ) : leaves.length === 0 ? (
                            <tr><td colSpan="6" className="px-6 py-20 text-center text-sm text-ink/40">No leave requests.</td></tr>
                        ) : (
                            leaves.map((l) => (
                                <tr key={l.id} className="border-b border-ink/10 hover:bg-bone/70" data-testid={`leave-row-${l.id}`}>
                                    <td className="px-6 py-4 text-sm text-ink font-medium">{l.employee_name}</td>
                                    <td className="px-6 py-4 text-sm text-ink/80">{l.leave_type}</td>
                                    <td className="px-6 py-4 text-sm text-ink/80">{l.start_date} → {l.end_date}</td>
                                    <td className="px-6 py-4 text-sm text-ink/60 hidden md:table-cell max-w-sm truncate">{l.reason || "—"}</td>
                                    <td className="px-6 py-4">
                                        <LeavePill status={l.status} />
                                    </td>
                                    <td className="px-6 py-4">
                                        <div className="flex items-center justify-end gap-1">
                                            {!isEmployee && l.status !== "approved" && (
                                                <button
                                                    onClick={() => setStatus(l.id, "approved")}
                                                    className="p-2 text-ink/60 hover:text-green-700 hover:bg-green-50"
                                                    title="Approve"
                                                    data-testid={`leave-approve-${l.id}`}
                                                >
                                                    <Check className="w-4 h-4" />
                                                </button>
                                            )}
                                            {!isEmployee && l.status !== "rejected" && (
                                                <button
                                                    onClick={() => setStatus(l.id, "rejected")}
                                                    className="p-2 text-ink/60 hover:text-destructive hover:bg-destructive/5"
                                                    title="Reject"
                                                    data-testid={`leave-reject-${l.id}`}
                                                >
                                                    <Ban className="w-4 h-4" />
                                                </button>
                                            )}
                                            {!isEmployee && (
                                                <button
                                                    onClick={() => onDelete(l.id)}
                                                    className="p-2 text-ink/60 hover:text-destructive hover:bg-destructive/5"
                                                    title="Delete"
                                                    data-testid={`leave-delete-${l.id}`}
                                                >
                                                    <Trash2 className="w-4 h-4" />
                                                </button>
                                            )}
                                            {isEmployee && (
                                                <span className="text-xs text-ink/40">View only</span>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>

            {open && (
                <div className="fixed inset-0 z-50 bg-ink/40 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setOpen(false)} data-testid="leave-dialog">
                    <form
                        onSubmit={onCreate}
                        onClick={(e) => e.stopPropagation()}
                        className="bg-white border border-ink/10 w-full max-w-lg animate-fade-in-up"
                    >
                        <div className="flex items-center justify-between px-8 py-6 border-b border-ink/10">
                            <h3 className="font-display font-bold text-xl">New leave request</h3>
                            <button type="button" onClick={() => setOpen(false)} className="p-2 text-ink/60 hover:bg-ink/5" data-testid="leave-dialog-close">
                                <X className="w-5 h-5" />
                            </button>
                        </div>
                        <div className="px-8 py-6 space-y-5">
                            <div>
                                <label className="overline block mb-2">Employee *</label>
                                <EmployeePicker employees={employees} value={form.employee_id} onChange={(v) => setForm({ ...form, employee_id: v })} className="py-2.5" testid="leave-employee-input" />
                            </div>
                            <div>
                                <label className="overline block mb-2">Leave type</label>
                                <select
                                    value={form.leave_type}
                                    onChange={(e) => setForm({ ...form, leave_type: e.target.value })}
                                    className="w-full border border-ink/15 py-2.5 px-3 text-sm bg-white focus:border-klein focus:outline-none"
                                    data-testid="leave-type-input"
                                >
                                    <option>Paid</option>
                                    <option>Sick</option>
                                    <option>Casual</option>
                                    <option>Unpaid</option>
                                </select>
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="overline block mb-2">Start *</label>
                                    <input
                                        type="date"
                                        required
                                        value={form.start_date}
                                        onChange={(e) => setForm({ ...form, start_date: e.target.value })}
                                        className="w-full border border-ink/15 py-2.5 px-3 text-sm focus:border-klein focus:outline-none"
                                        data-testid="leave-start-input"
                                    />
                                </div>
                                <div>
                                    <label className="overline block mb-2">End *</label>
                                    <input
                                        type="date"
                                        required
                                        value={form.end_date}
                                        onChange={(e) => setForm({ ...form, end_date: e.target.value })}
                                        className="w-full border border-ink/15 py-2.5 px-3 text-sm focus:border-klein focus:outline-none"
                                        data-testid="leave-end-input"
                                    />
                                </div>
                            </div>
                            <div>
                                <label className="overline block mb-2">Reason</label>
                                <textarea
                                    rows={3}
                                    value={form.reason}
                                    onChange={(e) => setForm({ ...form, reason: e.target.value })}
                                    className="w-full border border-ink/15 py-2.5 px-3 text-sm focus:border-klein focus:outline-none resize-none"
                                    data-testid="leave-reason-input"
                                />
                            </div>
                        </div>
                        <div className="px-8 py-5 border-t border-ink/10 flex items-center justify-end gap-3">
                            <button type="button" onClick={() => setOpen(false)} className="px-5 py-2.5 border border-ink/20 text-sm hover:bg-ink/5" data-testid="leave-cancel-button">
                                Cancel
                            </button>
                            <button type="submit" disabled={saving} className="px-5 py-2.5 bg-klein text-white text-sm hover:bg-klein/90 disabled:opacity-60" data-testid="leave-save-button">
                                {saving ? "Saving…" : "Submit request"}
                            </button>
                        </div>
                    </form>
                </div>
            )}
        </div>
    );
}

function LeavePill({ status }) {
    const map = {
        pending: { label: "Pending", cls: "bg-yellow-50 text-yellow-700 border-yellow-200" },
        hr_verified: { label: "HR Verified", cls: "bg-indigo-50 text-indigo-700 border-indigo-200" },
        approved: { label: "Approved", cls: "bg-green-50 text-green-700 border-green-200" },
        rejected: { label: "Rejected", cls: "bg-red-50 text-red-700 border-red-200" },
        cancelled: { label: "Cancelled", cls: "bg-ink/5 text-ink/70 border-ink/10" },
    };
    const s = map[status] || { label: status, cls: "bg-ink/5 text-ink/70 border-ink/10" };
    return (
        <span className={`inline-flex items-center px-2 py-1 border text-[11px] uppercase tracking-[0.15em] ${s.cls}`}>
            {s.label}
        </span>
    );
}
